import useAmplitude from 'core/hooks/useAmplitude';
import { useTranslate } from 'core/hooks/useTranslate';
import { renderProgress } from 'core/utils/jobs';
import { contentHasProgress, contentIsActivity, getLessonItemImageCover, isDocumentContent } from 'core/utils/lessonItems';
import { CONTENT_TYPES } from 'core/variables/constant';
import { LESSON_ITEM_STATUS } from 'core/variables/lessonItems';
import moment from 'moment';
import { useEffect, useState } from 'react';

function useMaterialPreviewBodyStudent({ course, material, evaluationPeriodPublished, onClickOpenDetailedView }) {
  const { t } = useTranslate();
  const { sendAmplitudeEvent, AMPLITUDE_EVENT } = useAmplitude();
  const {
    score,
    jobStatus,
    duration,
    deliver_at,
    content_type_guid = material.content_type_guid || material.content_type,
    useTangerineViewerInPreview,
    content_guid,
    evaluated_by,
    can_deliver_late,
    progress,
    didacticTypeCover,
    job,
  } = material;

  const [jobDeliveredAt, setJobDeliveredAt] = useState(null);

  useEffect(() => {
    if (job && job.deliver_at) {
      setJobDeliveredAt(job.deliver_at);
    }
  }, [job]);

  useEffect(() => {
    if (material.content_type_guid === CONTENT_TYPES.LINK && material.is_embed === 1) {
      const extraParams = { content_type: material.type, content_guid: material.content_guid, from: 'lesson' };
      sendAmplitudeEvent(AMPLITUDE_EVENT.OPEN_CONTENT, extraParams, course);
    }
  }, [material]);

  const statusTypes = [
    { status: 'pending', text: 'Pending', style: 'primary' },
    { status: 'delivered', text: 'Waiting for correction', style: 'secondary' },
    { status: 'not-available', text: 'Not available', style: 'disabled' },
  ];

  function getMetadataInfo(material) {
    const metadata = [
      {
        icon: 'calendar',
        text: t('lesson:Due date'),
        description: material.deliver_at ? moment(material.deliver_at).format('DD/MM/YYYY HH:mm') : t('lesson:No due date'),
        isLate: moment().isAfter(material.deliver_at),
      },
      {
        icon: 'timer',
        text: t('lesson:Deliver late'),
        description: material.can_deliver_late ? t('lesson:Allowed') : t('lesson:Not allowed'),
      },
    ];

    if (material.duration) {
      metadata.push({
        icon: 'clock',
        text: t('lesson:Duration'),
        description: material.duration + ' min',
      });
    }

    if (material.evaluation_category_name) {
      metadata.push({
        icon: 'bookmarks',
        text: 'Category',
        description: material.evaluation_category?.name,
      });
    }

    return metadata;
  }

  function getActivityStatusPending(jobStatus, deliver_at, can_deliver_late) {
    if (jobStatus === LESSON_ITEM_STATUS.DELIVERED) return statusTypes[1];
    const includeInGradebook = material?.include_in_gradebook !== false;
    const shouldBlock = evaluationPeriodPublished && includeInGradebook;
    if (
      (jobStatus === LESSON_ITEM_STATUS.PENDING && moment().isAfter(deliver_at) && !can_deliver_late) ||
      (jobStatus === LESSON_ITEM_STATUS.PENDING && shouldBlock)
    )
      return statusTypes[2];
    if (jobStatus === LESSON_ITEM_STATUS.PENDING) return statusTypes[0];

    return statusTypes[0];
  }

  function getButtonInfo(jobStatus, progress, content_type_guid, deliver_at, can_deliver_late) {
    if (contentHasProgress(content_type_guid) && progress * 100 > 0 && progress * 100 < 100) return { text: 'Continue', style: 'primary' };
    if (content_type_guid === CONTENT_TYPES.XAPI) return { text: 'View activity', style: 'primary' };
    const includeInGradebook = material?.include_in_gradebook !== false;
    const shouldBlock = evaluationPeriodPublished && includeInGradebook;
    if (jobStatus === LESSON_ITEM_STATUS.PENDING && shouldBlock) return { text: 'View activity', isDisabled: true, style: 'disabled' };
    if (jobStatus === LESSON_ITEM_STATUS.PENDING && moment().isBefore(deliver_at)) return { text: 'Start', style: 'primary' };
    if (jobStatus === LESSON_ITEM_STATUS.PENDING && moment().isAfter(deliver_at) && can_deliver_late) return { text: 'Start', style: 'primary' };
    if (jobStatus === LESSON_ITEM_STATUS.PENDING && moment().isAfter(deliver_at))
      return { text: 'View activity', ...(!can_deliver_late ? { isDisabled: true, style: 'disabled' } : { style: 'primary-ghost' }) };
    if (jobStatus === LESSON_ITEM_STATUS.PENDING && !deliver_at) return { text: 'Start', style: 'primary' };
    if (jobStatus === LESSON_ITEM_STATUS.DELIVERED && !evaluated_by) return { text: 'View my answers', style: 'primary-ghost' };
    if (jobStatus === LESSON_ITEM_STATUS.EVALUATED) return { text: 'View results', style: 'primary-ghost' };

    return { text: 'View activity', style: 'primary-ghost' };
  }

  function getAttentionCardInfo() {
    const states = {
      pending: {
        icon: 'emoji-smilling',
        title: 'You can work on this activity',
        text: 'Remember to deliver it when you finish',
        color: 'light',
      },
      pendingWithDuration: {
        icon: 'emoji-clock',
        title: 'This activity has a duration of {{duration}} minutes',
        duration: duration,
        text: "Once you enter you won't be able to do it again",
        color: 'light',
      },
      pendingRedo: {
        icon: 'emoji-face-with-sweat',
        title: 'Something was wrong!',
        text: 'You have to do this activity again',
        color: 'yellow',
      },
      pendingAfterDeliver: {
        icon: 'emoji-astonished',
        title: "It's getting late!",
        text: 'The deliver date has passed but you can still deliver it, work on it!',
        color: 'yellow',
      },
      notAvailableLate: {
        icon: 'emoji-warning',
        title: 'Is Late! You can no longer work on this activity',
        text: 'Deliver date has passed',
        color: 'red',
      },
      deliveredLate: {
        icon: 'emoji-astonished',
        title: 'You delivered late!',
        text: 'You will see the results once the teacher publishes them',
        color: 'light',
      },
      delivered: {
        icon: 'emoji-party',
        title: 'You delivered!',
        text: 'You will see the results once the teacher publishes them',
        color: 'light',
      },
      evaluated: {
        icon: 'emoji-star',
        title: 'Grades are ready!',
        text: 'You can see your results',
        color: 'light',
      },
      notAvailable: {
        icon: 'emoji-forbidden',
        title: 'Activity is closed',
        text: 'This activity is no longer available',
        color: 'red',
      },
    };
    const includeInGradebook = material?.include_in_gradebook !== false;
    const shouldBlock = evaluationPeriodPublished && includeInGradebook;
    if (jobStatus === LESSON_ITEM_STATUS.PENDING && shouldBlock) return states.notAvailable;
    if (jobStatus === LESSON_ITEM_STATUS.PENDING && moment().isAfter(deliver_at) && can_deliver_late) return states.pendingAfterDeliver;
    if (jobStatus === LESSON_ITEM_STATUS.PENDING && moment().isAfter(deliver_at) && !can_deliver_late) return states.notAvailableLate;
    if (jobStatus === LESSON_ITEM_STATUS.PENDING && evaluated_by) return states.pendingRedo;
    if (jobStatus === LESSON_ITEM_STATUS.PENDING && !duration) return states.pending;
    if (jobStatus === LESSON_ITEM_STATUS.PENDING && duration) return states.pendingWithDuration;
    if (jobStatus === LESSON_ITEM_STATUS.DELIVERED && moment(jobDeliveredAt).isAfter(deliver_at)) return states.deliveredLate;
    if (jobStatus === LESSON_ITEM_STATUS.DELIVERED) return states.delivered;
    if (jobStatus === LESSON_ITEM_STATUS.EVALUATED) return states.evaluated;
    return states.pending;
  }

  function onClickOpenContent() {
    onClickOpenDetailedView(material);
  }

  function onClickOpenInNewTab() {
    const jobParam = job?.id ? `&jobGuid=${job.id}` : '';
    window.open(`/viewerfull/${material?.content_guid}?lessonItem=${material?.lesson_item_guid}&hideCover=1${jobParam}`, '_blank');
  }

  return {
    image: getLessonItemImageCover(content_type_guid, deliver_at, can_deliver_late, evaluationPeriodPublished, jobStatus, contentIsActivity(content_type_guid)),
    didacticTypeCover,
    score,
    jobStatus,
    deliver_at,
    content_type_guid,
    useTangerineViewerInPreview,
    content_guid,
    attentionCardIcon: getAttentionCardInfo().icon,
    attentionCardTitle: getAttentionCardInfo().title,
    attentionCardIconText: getAttentionCardInfo().text,
    attentionCardColor: getAttentionCardInfo().color,
    attentionCardDuration: getAttentionCardInfo().duration,
    buttonText: getButtonInfo(jobStatus, progress, content_type_guid, deliver_at, can_deliver_late).text,
    buttonStyle: getButtonInfo(jobStatus, progress, content_type_guid, deliver_at, can_deliver_late).style,
    buttonIsDisabled: getButtonInfo(jobStatus, progress, content_type_guid, deliver_at, can_deliver_late).isDisabled,
    renderProgress: renderProgress(progress),
    activityStatus: getActivityStatusPending(jobStatus, jobDeliveredAt, can_deliver_late),
    progress,
    isDocument: isDocumentContent(content_type_guid),
    getMetadataInfo,
    onClickOpenContent,
    onClickOpenInNewTab,
  };
}

export default useMaterialPreviewBodyStudent;
